// Which feature sent the visitor to the login modal.
//
// The value rides along with OTP verification and the Go backend stamps it onto
// the new user's metadata as their acquisition `source`, exactly the way the
// public API calculators already mark theirs. That is what lets marketing pull
// "phone numbers that came in through the compatibility calculator" without any
// new table or endpoint.
//
// sessionStorage rather than a prop, because that is already how this codebase
// carries context into login: AuthContext.verifyMsg91Otp reads `guestVariant`
// the same way. It also survives the Google OAuth full-page reload, which a
// React prop cannot.
//
// Only known values are accepted. This string ends up in user metadata, so
// letting a caller write anything would let a bug (or a console) put arbitrary
// keys into the auth record, and the Go side whitelists it again for the same
// reason.

const KEY = "auraAuthSource";

export const AUTH_SOURCES = ["compatibility_check"] as const;
export type AuthSource = (typeof AUTH_SOURCES)[number];

export function setAuthSource(source: AuthSource) {
  try {
    sessionStorage.setItem(KEY, source);
  } catch {
    /* storage blocked: the signup is still fine, it just is not attributed */
  }
}

/**
 * Read the pending source WITHOUT clearing it.
 *
 * Not single-use on purpose. A visitor can fail an OTP, resend, and verify
 * again, and every one of those attempts must carry the same attribution — a
 * read-and-clear would credit the first attempt and silently drop the retry,
 * which is the attempt that actually succeeds.
 */
export function peekAuthSource(): AuthSource | null {
  try {
    const v = sessionStorage.getItem(KEY);
    return (AUTH_SOURCES as readonly string[]).includes(v || "")
      ? (v as AuthSource)
      : null;
  } catch {
    return null;
  }
}

/** Call once login has actually succeeded. */
export function clearAuthSource() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to do */
  }
}
